// functions.js
// Zentraler PDF-Exporter für Tabellen (nutzt jsPDF + jspdf-autotable)

import jsPDF from 'jspdf';
import 'jspdf-autotable';

/**
 * Exportiert beliebige Tabellendaten als PDF mit Seitenfuß/Paginierung.
 * @param {Object} opts
 * @param {Array<Object>} opts.rows – Datenzeilen (bereits formatiert als Strings/Numbers)
 * @param {Array<{header:string, dataKey:string}>} opts.columns – Spalten-Definition
 * @param {string} opts.title – Überschrift oben links
 * @param {string} opts.filename – Dateiname inkl. .pdf
 * @param {Object} [opts.jsPDFOptions] – jsPDF Options (default: landscape/pt/A4)
 * @param {Object} [opts.tableOptions] – autoTable Options (theme/styles/...)
 * @returns {jsPDF} – Das jsPDF-Dokument (wird außerdem gespeichert)
 */
export function exportTableToPdf({
  rows,
  columns,
  title,
  filename,
  jsPDFOptions = { orientation: 'landscape', unit: 'pt', format: 'a4' },
  tableOptions = {},
}) {
  const doc = new jsPDF(jsPDFOptions);

  // Titel
  doc.setFontSize(14);
  if (title) doc.text(String(title), 40, 32);

  // Tabelle
  doc.autoTable({
    columns,
    body: rows,
    startY: title ? 50 : 20,
    theme: 'grid',
    styles: { fontSize: 8, cellPadding: 5, overflow: 'linebreak' },
    headStyles: { fontStyle: 'bold' },
    // Seitenzahl unten rechts
    didDrawPage: function () {
      const pageCount = doc.internal.getNumberOfPages();
      const pageSize = doc.internal.pageSize;
      doc.setFontSize(9);
      doc.text(
        `Seite ${doc.internal.getCurrentPageInfo().pageNumber} / ${pageCount}`,
        pageSize.getWidth() - 110,
        pageSize.getHeight() - 15
      );
    },
    ...tableOptions,
  });

  // Speichern
  doc.save(filename || 'export.pdf');
  return doc;
}

/**
 * Wetter-spezifischer Wrapper: baut Spalten/Zeilen aus rawData + Keys + Label-Fn.
 * @param {Object} opts
 * @param {Array<Object>} opts.rawData – Rohdaten (mit { datum, <uiKey>... })
 * @param {Array<string>} opts.allKeys – alle Parameter-Keys (z.B. GROUPS.ALL)
 * @param {(key:string)=>string} opts.getLabel – liefert Spaltenlabel zu einem Key
 * @param {(iso:string|Date)=>string} opts.formatDate – Datum formatieren (z.B. formatAT)
 * @param {string} opts.stationName – bereits hübscher Name (prettyStationName(ort))
 * @param {string} opts.filename – gewünschter Dateiname (inkl. .pdf)
 */
export function exportWeatherDataToPdf({
  rawData,
  allKeys,
  getLabel,
  formatDate,
  stationName,
  filename,
}) {
  if (!Array.isArray(rawData) || rawData.length === 0) return;

  const columns = [
    { header: 'Datum', dataKey: 'datum' },
    ...allKeys.map((key) => ({
      header: (getLabel && getLabel(key)) || key,
      dataKey: key,
    })),
  ];

  const body = rawData.map((r) => {
    const row = { datum: formatDate ? formatDate(r.datum) : String(r.datum) };
    allKeys.forEach((k) => { row[k] = r[k] ?? ''; });
    return row;
  });

  return exportTableToPdf({
    rows: body,
    columns,
    title: `Wetterdaten – ${stationName} (ALLE Daten)`,
    filename: filename || `Wetterdaten_alle.pdf`,
  });
}
